import {
  DATE_FORMAT_PARAM,
  DAY_NAMES,
  MONTH_NAMES,
  SHORT_DAY_NAMES,
  SHORT_MONTH_NAMES,
} from "../constant/common";
import dayjs from "./dayjs";
import { number as formatNumber } from "@euroland/format-number";
import {
  isValidNegativeNumberFormat,
  loadCustomStylesheet,
  setVariableByTemplate,
} from "@euroland/libs";

import variableStyles from "../styles/variables-style.json";
import variableStylesCommon from "@euroland/react-utils/dist/styles/variables-style.json";


window.dayjs = dayjs
/**
 * @returns
 */
export function getLanguage() {
  let locale = window.appSettings?.language;
  if (!locale || !locale.length) {
    locale = "en-gb";
  }
  return locale;
}

/**
 * remove
 * @param { number | Date} value
 * @param { import('../..').CompanySetting } settings
 * @returns { import('dayjs').Dayjs }
 */
export function getDateTime(value, settings) {
  let timeZone = settings.timezone;
  return dayjs.utc(value).tz(timeZone);
}

/**
 * @param { number | Date} value
 * @param { import('../..').CompanySetting } settings
 * @returns { string }
 */
export function formatDateTime(value, settings, isLongDate = false) {
  value = value || new Date();
  let formatDate = isLongDate ? settings.format.longDate : settings.format.shortDate;
  let timeZone = settings.timezone;
  return dayjs.utc(value).tz(timeZone).format(formatDate);
}

/**
 * @param { string } utcDateString
 * @param { import('../..').CompanySetting } settings
 * @returns { string }
 */
export function getDateFromDateTimeString(utcDateString) {
  if (utcDateString === "") return   
  return dayjs.utc(utcDateString).format('YYYY-MM-DD');
}

/**
 * @param {{
 * decimalDigits: import("../..").IFormatSetting['decimalDigits']
 * decimalSeparator: import("../..").IFormatSetting['decimalSeparator']
 * thousandsSeparator:import("../..").IFormatSetting['thousandsSeparator']
 * }} param
 * @returns
 */
export const convertFormatNumber = ({
  decimalDigits = 0,
  decimalSeparator = ".",
  thousandsSeparator = ",",
}) => {
  switch (decimalDigits) {
    case 0:
      return `#,##0`;
    default:
      return `#,##0.${Array.from({ length: decimalDigits }).reduce(s => `${s}0`, "")}`;
  }
};

/**
 * @param { number } value
 * @param { import("../..").IFormatSetting} format
 * @returns
 */
export function convertNumber(value, format) {
  return formatNumber(value, convertFormatNumber({ ...format, decimalDigits: 0 }));
}

/**
 * @param { number } value
 * @param { import("../..").IFormatSetting } format
 * @returns { string }
 */
export function convertNumberDecimal(value, format) {
  return formatNumber(value, convertFormatNumber(format));
}

/**
 * @param { string } stringValue
 * @param { import("../..").INegativeNumberFormat } formatNegativeNumber
 * @returns { string }
 */
export function formatNegativeNumber(stringValue, formatNegativeNumber) {
  if (!isValidNegativeNumberFormat(formatNegativeNumber)) {
    formatNegativeNumber = "-n";
  }
  return formatNegativeNumber.replace("n", stringValue);
}

// TODO
/**
 * @param {*} params
 * @param {*} settings
 */
export function loadSettingEUCalendar(params = {}, settings) {
  const { language, isRtl, translations } = window.appSettings;
  const { useLatinNumber } = settings.data.settings || {};
  const dateNames = JSON.parse(window.appSettings.dateNames);

  const mn = dateNames?.monthNames ? dateNames.monthNames.filter(m => !!m) : MONTH_NAMES;
  const smn = dateNames?.shortMonthNames ? dateNames.shortMonthNames.filter(m => !!m) : SHORT_MONTH_NAMES;
  const dn = dateNames?.dayNames ? dateNames.dayNames.filter(m => !!m) : DAY_NAMES;
  const sdn = dateNames?.shortDayNames ? dateNames.shortDayNames.filter(m => !!m) : SHORT_DAY_NAMES;

  window.Calendar.LANG(language || "en", language || "English", {
    fdow: 1, // first day of week
    goToday: translations["today"] || "Today",
    today: translations["today"] || "Today",
    wk: "wk",
    weekend: "0,6",
    AM: translations["AM"],
    PM: translations["PM"],
    mn,
    smn,
    dn,
    sdn,
    isRtl,
    useArabicNumeral: isRtl ? !useLatinNumber : false,
    ...params,
  });

  return settings;
}

/**
 * Get total return data if dividend checkbox is checked
 * @param {Array} shareData - raw share data. It should have data structure is "[ [xValue1, yValue1], [xValue2, yValue2] ]"
 * @param {Array} dividendData - raw dividend data. It should have data structure is "[ [xValue1, yValue1], [xValue2, yValue2] ]"
 * @return {Array} raw total return data. It should have data structure is "[ [xValue1, yValue1], [xValue2, yValue2] ]"
 */
export function getTotalReturnData(shareData, dividendData = [], shareBought) {
  if (!Array.isArray(shareData) || shareData.length <= 0) return null;

  let totalReturnData = [];

  // There are no dividends, Total Return data is same with share data
  // if (!Array.isArray(dividendData) || dividendData.length <= 0) {
  // totalReturnData = [...shareData];
  // }

  let lastItemIndexFoundDividend = -1; // Last found index of item in dividends array. Keep the index for quick search
  let dividendItemIndex; // Current index of item in dividends array
  let lastTotalReturnValue;
  let multiplier = 1;
  let factor = 0;

  shareData.forEach(function (share, i) {
    let isMoreThan = false;
    dividendItemIndex = -1; //reset index
    for (let j = lastItemIndexFoundDividend + 1; j < dividendData.length; j++) {
      let shareDate = new Date(share[0]);
      let dividendDate = new Date(dividendData[j][0]);

      if (
        shareDate.getDate() === dividendDate.getDate() &&
        shareDate.getMonth() === dividendDate.getMonth() &&
        shareDate.getYear() === dividendDate.getYear()
      ) {
        isMoreThan = false;
        dividendItemIndex = j;
        break;
      } else if (
        shareDate.getDate() > dividendDate.getDate() &&
        shareDate.getMonth() >= dividendDate.getMonth() &&
        shareDate.getYear() >= dividendDate.getYear()
      ) {
        isMoreThan = true;
        dividendItemIndex = j;
        break;
      }
    }

    // calculate factor
    if (dividendItemIndex != -1 && lastItemIndexFoundDividend < dividendItemIndex) {
      // Found the new dividend
      lastItemIndexFoundDividend = dividendItemIndex;
      let lastDPS = dividendData[dividendItemIndex][1];
      if (isMoreThan && i > 0) {
        factor = 1 + lastDPS / (shareData[i - 1][1] || 1);
      } else {
        factor = 1 + lastDPS / (shareData[i][1] || 1);
      }

      if (i > 0) multiplier = multiplier * factor;
    }

    totalReturnData.push([shareData[i][0], multiplier * shareData[i][1] * shareBought]);
  });

  return totalReturnData;
}

export class InvestmentCalChart {
  static PeriodChartType = {
    Year: 5, // more then 3 year is Year period
    Quarter: 4, //less then 3 year is Quarter period
    Month: 3, //less then 1 year is Month period
    Week: 2, // less than 3 months is Week period
    Day: 1, // less than 2 weeks is Day period
  };
}

/**
 * calculate for share data for PeriodChart
 * @param {Object} shareData - raw share price data recieved from server
 * @param {Object} dividendData - raw dividend data recieved from server. Null if "Dividend reinvested" checkbox is not checked
 * @param {string} shareBought - number of share
 */
export function calculateShareData(shareData, shareBought) {
  let seriesData = [];

  let startDate = dayjs(shareData[0][0]).startOf("day");
  let endDate = dayjs(shareData[shareData.length - 1][0]).startOf("day");

  // detect periodType: Day, Month, Week, Quarter of Year
  let periodType = InvestmentCalChart.PeriodChartType.Day;

  if (startDate.add(2, "weeks").isAfter(endDate)) {
    periodType = InvestmentCalChart.PeriodChartType.Day;
  } else if (startDate.add(3, "months").isAfter(endDate)) {
    periodType = InvestmentCalChart.PeriodChartType.Week;
  } else if (startDate.add(1, "year").isAfter(endDate)) {
    periodType = InvestmentCalChart.PeriodChartType.Month;
  } else if (startDate.add(3, "years").isAfter(endDate)) {
    periodType = InvestmentCalChart.PeriodChartType.Quarter;
  } else {
    periodType = InvestmentCalChart.PeriodChartType.Year;
  }

  InvestmentCalChart.PeriodicalSelectedType = periodType;
  //filter data
  let shareDataLength = shareData.length - 1;
  // period type is day and data length = 0.
  if (periodType === InvestmentCalChart.PeriodChartType.Day || shareDataLength === 0) {
    seriesData = [...shareData];
  } else {
    let lastPeriodDate = new Date();
    seriesData = shareData.filter(function (share, i) {
      let result = false;
      let shareDate = dayjs(share[0]);
      //check share date
      if (shareDate.isValid()) {
        let otherElement = dayjs();
        // is last elements in array
        if (i == shareDataLength) {
          return true;
        } else {
          otherElement = dayjs(shareData[i + 1][0]);
        }
        let shareMonth = shareDate.get("M");
        let otherMonth = otherElement.get("M");
        switch (periodType) {
          case InvestmentCalChart.PeriodChartType.Week:
            result = shareDate.week() !== otherElement.week();
            break;
          case InvestmentCalChart.PeriodChartType.Month:
            result = shareMonth !== otherMonth;
            break;
          case InvestmentCalChart.PeriodChartType.Quarter:
            result = shareDate.quarter() !== otherElement.quarter();
            break;
          case InvestmentCalChart.PeriodChartType.Year:
            result = shareDate.get("year") !== otherElement.get("year");
            break;
          default:
            break;
        }
        if (result) {
          lastPeriodDate = shareDate;
        }
      }
      return result;
    });
  }
  let xAxisCategory = [];
  for (let i = 0; i < seriesData.length; i++) {
    seriesData[i][1] = seriesData[i][1] * shareBought;
    let serieDate = seriesData[i][0];
    xAxisCategory.push(serieDate);
  }
  // start date
  xAxisCategory[0] = shareData[0][0];
  //return result
  return { seriesData: seriesData, xAxisCategory: xAxisCategory, periodType };
}

/**
 * @param {{datas: []}} datas
 * @returns {
 *  value: number;
 *  dividendReinvested: number;
 *  sharePrice: number;
 *  change: number;
 *  changePer: number;
 * }
 */
export const getNormalizedDataColumChart = (datas = []) => {
  return datas
    .map(d => ({ date: d[0], value: d[1] }))
    .reduce(
      (s, data, i) => {
        if (i === 0) {
          s.previous.push(data);
          s.offset.push(data);
          s.increase.push({ ...data, value: 0 });
          s.decrease.push({ ...data, value: 0 });
          return s;
        }
        s.previous.push({
          ...data,
          value: s.previous[i - 1].value + s.increase[i - 1].value - s.decrease[i - 1].value,
        });
        if (data.value > s.previous[i].value) {
          s.increase.push({ ...data, value: data.value - s.previous[i].value });
          s.decrease.push({ ...data, value: 0 });
          s.offset.push(s.previous[i]);
        } else {
          s.decrease.push({ ...data, value: s.previous[i].value - data.value });
          s.increase.push({ ...data, value: 0 });
          s.offset.push({ ...data, value: data.value });
        }
        return s;
      },
      { previous: [], offset: [], increase: [], decrease: [] }
    );
};

/**
 * @param {*} serieDate
 * @param {*} periodType
 * @returns
 */
export const getXAxisCategory = (serieDate, periodType) => {
  switch (periodType) {
    case InvestmentCalChart.PeriodChartType.Day:
      return serieDate.format("DD/MM/YY");
    case InvestmentCalChart.PeriodChartType.Week:
      return serieDate.format("DD/MM/YY");
    case InvestmentCalChart.PeriodChartType.Month:
      return serieDate.format("MM/YY");
    case InvestmentCalChart.PeriodChartType.Quarter:
      return serieDate.format("MM/YY");
    case InvestmentCalChart.PeriodChartType.Year:
      return serieDate.format("YYYY");
    default:
      return;
  }
};

/**
 * @param {*} datas
 * @returns
 */
export const sortDataByDate = (datas = []) => {
  return datas.sort((d1, d2) => {
    if (d1.date < d2.date) {
      return -1;
    }
    if (d1.date > d2.date) {
      return 1;
    }
    return 0;
  });
};

/**
 * @param {*} settings
 * @returns
 */
export function loadConfiguredCustomStylesheet(settings) {
  loadCustomStylesheet(
    window.appSettings.customStylesheets,
    settings.data.settings.styleURI,
    window.appSettings.customStylesheetUrlBase
  );
  return settings;
}

export function loadVariableStylesByTemplate(settings) {
  const template = settings.data.settings.template;
  setVariableByTemplate(template, variableStylesCommon, variableStyles).injectTo(document.head, true);
  return settings;
}

/**
 * @param {*} date
 * @returns
 */
export const getDateParam = date => {
  return dayjs.utc(dayjs(date).format(DATE_FORMAT_PARAM)).toISOString();
};

export const formatNumberBySettings = (value, settings) => {
  const { decimalDigits, thousandsSeparator, decimalSeparator, positiveFormatPattern } = settings;
  if (!decimalDigits && typeof decimalDigits !== "number") {
    return formatNumber(value);
  }
  const convertedNumber = convertFormatNumber({ decimalDigits });

  const options = {
    groupingSeparator: thousandsSeparator,
    decimalSeparator,
  };

  if (!!positiveFormatPattern) {
    options.positiveFormatPattern = positiveFormatPattern;
  }

  return formatNumber(value, convertedNumber, options);
};

export {
  classNames,
  pickBy,
  replaceKey,
  mergeObject,
  observerWindowResize,
  clickWithoutMove,
  getRealEventName,
} from "@euroland/libs";
